import { useGatedShowContent, useMobile } from "@with-nx/hooks-n-helpers";
import { useRegion } from "@with-nx/region";
import { DesignedButton, DesignedSelect, Rule } from "@with-nx/simple-ui/atoms";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { useState } from "react";
import { Box } from "simple-effing-primitive-layout";

import { TabbedAuthForm } from "../auth/TabbedAuthForm";

const organizationTypeOptions = [
  ["Elementary School", "Elementary School"],
  ["Middle School", "Middle School"],
  ["High School", "High School"],
  ["University", "University"],
  ["Community Theatre", "Community Theatre"],
  ["Military", "Military"],
  ["Religious", "Religious"],
  ["Camp", "Camp"],
  ["Childrens Theatre", "Childrens Theatre"],
  ["Professional", "Professional"],
  ["Dance", "Dance"],
  ["Other", "Other"],
];

export type WaitListFormValues = {
  organization_type?: string;
  region?: string;
};

const WaitListForm = ({ showId, productType = "scenic-projections" }) => {
  const { createWaitList, saving, errorMessage } = useGatedShowContent(showId);

  const router = useRouter();
  const mobile = useMobile();
  const region = useRegion();

  const [values, _values] = useState<WaitListFormValues>({
    region: "🇺🇸 United States",
    organization_type: "",
  });

  const handleSubmit = async () => {
    try {
      await createWaitList(showId, productType);
      router.push("/waitlist/thank-you");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      top={24}
      parse={`br:10 oa:hidden ${mobile ? "ma:12" : ""}`}
      border="1px solid #FFFFFF1A"
    >
      <Box
        parse="pa:24"
        style={{
          borderBottom: "1px solid #2D3542",
          boxSizing: "border-box",
        }}
      >
        <Rule parse="!ll">Join Waitlist</Rule>
      </Box>
      <Box
        parse="pa:24"
        style={{
          borderBottom: "1px solid #2D3542",
          boxSizing: "border-box",
        }}
      >
        <Rule parse="!lm c:var(--font4)">
          To provide you with accurate updates, we need to ask a couple of
          questions.
        </Rule>
      </Box>

      <Box parse="pa:24" color="rgba(30, 36, 45, 0.5)">
        <Row gutter={[36, 12]}>
          <Col xs={24} sm={24}>
            <DesignedSelect
              label="Where is your venue located?"
              value={values.region}
              change={(region) => {
                _values({ ...values, region });
              }}
              options={region.regions?.map((i) => [i, i]) || []}
              bottom={12}
            />
          </Col>

          <Col xs={24} sm={24}>
            <DesignedSelect
              label="Which category describes your organization?"
              value={values.organization_type}
              change={(organization_type) =>
                _values({ ...values, organization_type })
              }
              bottom={20}
              empty={true}
              options={organizationTypeOptions}
              error={errorMessage}
            />
          </Col>
        </Row>

        <Row justify="end">
          <DesignedButton
            label="Notify Me When Available"
            press={handleSubmit}
            loading={saving}
          />
        </Row>
      </Box>
    </Box>
  );
};

export const WaitListFormContainer = ({
  restricted,
  showId,
  productType = "scenic-projections",
  title,
  description,
}: {
  restricted?: boolean;
  showId: string;
  productType?: string;
  title?: string;
  description?: string;
}) => {
  const mobile = useMobile();

  return (
    <Box top={48}>
      <Row gutter={[36, 18]} align="stretch" justify="center">
        <Col xs={24} sm={24} md={16} lg={14}>
          <Box parse={mobile ? "pa:24 d:flex fd:column a:center" : ""}>
            <Rule
              rule="hl"
              top={16}
              parse="c:?primary d:block mb:12"
              tag="span"
            >
              {title || "Join Waitlist"}
            </Rule>

            <Rule rule="ht" color="var(--font2)" top={24} parse="d:block mb:12">
              {description ||
                "This Scenic Projections is currently unavailable. Sign-up to be among the first to know when this title is released."}
            </Rule>

            <Box top={24}>
              <Rule rule="lm" color="var(--font3)">
                You must be signed-in in order to receive updates on
                availability for this scenic projection package.
              </Rule>
            </Box>
          </Box>
        </Col>
      </Row>
      <Row gutter={[36, 18]} align="stretch" justify="center">
        <Col xs={24} sm={24} md={16} lg={14}>
          {restricted ? (
            <TabbedAuthForm />
          ) : (
            <WaitListForm showId={showId} productType={productType} />
          )}
        </Col>
      </Row>
    </Box>
  );
};
